import * as Yup from "yup";
import {
  type Householder,
  type HouseholderQuote,
  HouseholderType,
} from "@/api_client";
import type { HouseholderEntryContent } from "@/services/cms/contentTypes/entry/HouseholderEntryContent";
import { getErrorMessageNew } from "@/utils/errorMessage";
import { AgeValidator } from "@/validators/shared/ageValidators";
import { validSSN } from "@/validators/shared/ssnValidator";

type ValidationMessage = {
  Key?: string | null;
  Value?: string | null;
};

const getValidationMessage = (
  messages: (ValidationMessage | null)[] | null | undefined,
  errorKey: string,
) => {
  const defaultError = `Missing error for: ${errorKey}`;
  if (!messages) {
    return defaultError;
  }

  return messages.find((item) => item?.Key === errorKey)?.Value ?? defaultError;
};

const HouseholdEntryValidationSchema = (
  content?: HouseholderEntryContent | null,
): Yup.ObjectSchema<
  Omit<
    Householder,
    "streetAddress" | "postalCode" | "city" | "householderInsuredWithIf"
  >
> =>
  Yup.object({
    householderType: Yup.mixed<HouseholderType>().required(
      getErrorMessageNew(content?.housingType?.ValidationMessages, "required"),
    ),
    livingSpace: Yup.number()
      .typeError(
        getErrorMessageNew(
          content?.livingSpace?.ValidationMessages,
          "required",
        ),
      )
      .required(
        getErrorMessageNew(
          content?.livingSpace?.ValidationMessages,
          "required",
        ),
      )
      .min(
        4,
        getErrorMessageNew(content?.livingSpace?.ValidationMessages, "minimum"),
      )
      .max(
        400,
        getErrorMessageNew(content?.livingSpace?.ValidationMessages, "maximum"),
      )
      .test(
        "wrong-format",
        getErrorMessageNew(
          content?.livingSpace?.ValidationMessages,
          "wrong-format",
        ),
        (val) => {
          const matched = /^\d+$/.exec(val.toString());
          return matched !== undefined && matched !== null;
        },
      ),
    isHousingCompany: Yup.boolean().optional(),
  });

export const HouseholderEntryValidationSchema = (
  content?: HouseholderEntryContent | null,
): Yup.ObjectSchema<
  Pick<
    HouseholderQuote,
    "productType" | "householder" | "policyHolderSsn" | "isHousingCompany"
  >
> =>
  Yup.object({
    productType: Yup.string().oneOf(["householder"]).required(),
    householder: HouseholdEntryValidationSchema(content).required(),
    policyHolderSsn: Yup.string()
      .required(
        getErrorMessageNew(
          content?.policyHolderSsn?.ValidationMessages,
          "required",
        ),
      )
      .test(
        "valid-ssn",
        getErrorMessageNew(
          content?.policyHolderSsn?.ValidationMessages,
          "valid-ssn",
        ),
        validSSN,
      )
      .test(
        "is-18+",
        getErrorMessageNew(
          content?.policyHolderSsn?.ValidationMessages,
          "is-18+",
        ),
        (ssn: string) => {
          const ageValidator = new AgeValidator(ssn);
          return ageValidator.isAtLeast18;
        },
      ),
    isHousingCompany: Yup.boolean().when("householder.householderType", {
      is: HouseholderType.PAIR_HOUSE,
      then: (schema) =>
        schema
          .required(
            getValidationMessage(
              content?.housingCompany?.ValidationMessages,
              "required",
            ),
          )
          .oneOf(
            [true],
            getValidationMessage(
              content?.housingCompany?.ValidationMessages,
              "not-housing-company",
            ),
          ),
      otherwise: (schema) => schema.optional(),
    }),
  });
